import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@material-tailwind/react';
import { ReactComponent as SuccessIcon } from '@assets/icons/Success_light.svg';
import VehicleLeft from '@assets/images/vehiclePhoto/car_passenger.png';
import VehicleRight from '@assets/images/vehiclePhoto/car_drivers.png';
import VehicleBack from '@assets/images/vehiclePhoto/car_back.png';
import BikeBack from '@assets/images/vehiclePhoto/bike_back.png';
import BikeLeft from '@assets/images/vehiclePhoto/bike_left.png';
import BikeRight from '@assets/images/vehiclePhoto/bike_right.png';
import CaravanBack from '@assets/images/vehiclePhoto/caravan_back.png';
import CaravanLeft from '@assets/images/vehiclePhoto/caravan_left.png';
import CaravanRight from '@assets/images/vehiclePhoto/caravan_right.png';
import TrailerBack from '@assets/images/vehiclePhoto/trailer_back.png';
import TrailerLeft from '@assets/images/vehiclePhoto/trailer_left.png';
import TrailerRight from '@assets/images/vehiclePhoto/trailer_right.png';
import BoatBack from '@assets/images/vehiclePhoto/boat-back.png';
import BoatLeft from '@assets/images/vehiclePhoto/boat-left.png';
import BoatRight from '@assets/images/vehiclePhoto/boat-right.png';
import clsx from 'clsx';
import _indexOf from 'lodash/indexOf';
import { useSellerStore } from '@stores';
import { GET_INSPECTION, GET_INSPECTION_CHECK, GET_INSPECTION_FILES, post } from '@services';
import {
  refreshInspectionData as refreshInspection,
  isVehicleAssetPhotoType,
  isMarinePhotoType,
  isInternalPhotoType,
  isInternalPhotosHidden,
  isInternalPhotosSuccess,
  areAllVehicleAssetPhotosDone,
  areAllConfiguredMarinePhotosDone,
  getCaptureSuccessMessage,
  getSalePriceContext,
  getVehicleSectionPageTitle,
  getMarinePhotoStepKey,
  getVehiclePhotoStepKey,
  isSellerCaptureSectionComplete
} from '@utils/vehicleUtils';
import {
  CardCaptureStepEnum,
  SellerPage,
  SellerPageTitle,
  VehiclePhoneMappings,
  MarinePhoneMappings,
  AssetTypesEnum
} from '@types';
import { VEHICLE_PHOTO_LIST, MARINE_PHOTO_LIST } from '@constants';
import {
  processFile,
  FILE_VALIDATION_CONFIGS,
  validateFile,
  getAssetSpecificContent,
  type ProcessFileMetrics
} from '@utils';
import FileTypeErrorModal from '@components/widgets/FileTypeErrorModal';

/**
 * CardCapture (and similar) step machine in parent: 0 = instruction/how-to,
 * 1 = live capture, 2 = result, 3 = review, 4 = this success screen.
 * Returning here after Continue exits the success UI and shows the instruction step again
 * for the same doc type until `init()` / navigation switches the user to the next task.
 */
const CARD_CAPTURE_INSTRUCTION_STEP = 0;

interface CaptureCompleteProps {
  type: string;
  vehiclePosition: number;
  setLoadImage: (image: string) => void;
  setUploadMetrics: (metrics?: ProcessFileMetrics) => void;
  primaryColor: string;
  setStep: (step: number) => void;
  setPage: (page: SellerPage) => void;
  init: (param?: string) => void;
}

const CaptureComplete: React.FC<CaptureCompleteProps> = ({
  type,
  vehiclePosition,
  setStep,
  setLoadImage,
  setUploadMetrics,
  primaryColor,
  setPage,
  init
}) => {
  const {
    odometer,
    vehicle,
    externalPhotos,
    setVehiclePosition,
    vehiclePhotoList,
    setVehicleImageFront,
    setVehicleImageDriver,
    setVehicleImageBack,
    setVehicleImagePassenger,
    setExternalPhotos,
    setVehicle,
    vehicleFront,
    vehiclePassenger,
    vehicleDriver,
    vehicleBack,
    vehicleFrontSeats,
    vehicleBackSeats,
    vehicleBoot,
    vehicleLivingArea,
    vehicleKitchen,
    vehicleSleepingArea,
    marineFront,
    marineBack,
    marineLeft,
    marineRight,
    setMarineImageFront,
    setMarineImageBack,
    setMarineImageLeft,
    setMarineImageRight,
    setActivePageTitle,
    documentTypes,
    assetType,
    inspectionId,
    setData,
    setInspectionChecks
  } = useSellerStore();

  const [nextVehiclePosition, setNextVehiclePosition] = useState(0);
  const [showFileTypeError, setShowFileTypeError] = useState(false);
  const [fileErrorMessage, setFileErrorMessage] = useState('');

  useEffect(() => {
    if (
      type === CardCaptureStepEnum.vehiclePhoto &&
      vehiclePosition <
        VehiclePhoneMappings[
          vehiclePhotoList[
            vehiclePhotoList.length - 1
          ] as keyof typeof VehiclePhoneMappings
        ]
    ) {
      const vehicleIndex = _indexOf(
        vehiclePhotoList,
        VEHICLE_PHOTO_LIST[vehiclePosition - 1]
      );
      const nextIndex =
        VehiclePhoneMappings[
          vehiclePhotoList[
            vehicleIndex + 1
          ] as keyof typeof VehiclePhoneMappings
        ];
      setNextVehiclePosition(nextIndex);
    } else if (
      type === CardCaptureStepEnum.marinePhoto &&
      vehiclePosition < 
        MarinePhoneMappings[
          vehiclePhotoList[
            vehiclePhotoList.length - 1
          ] as keyof typeof MarinePhoneMappings
        ]
    ) {
      // vehiclePosition is the 1-based side in MARINE_PHOTO_LIST; the required
      // list may skip sides, so look up the next entry the same way vehicles do.
      const marineIndex = _indexOf(
        vehiclePhotoList,
        MARINE_PHOTO_LIST[vehiclePosition - 1]
      );
      const nextPhoto = vehiclePhotoList[marineIndex + 1];
      const nextIndex =
        MarinePhoneMappings[nextPhoto as keyof typeof MarinePhoneMappings];
      setNextVehiclePosition(nextIndex);
    }
  }, [vehicle.vehiclePosition]);

  const captureImageBtn = useRef<HTMLInputElement>(null);

  const handleCaptureNewImage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const validationResult = validateFile(
        file,
        FILE_VALIDATION_CONFIGS.IMAGES_ONLY
      );

      if (!validationResult.isValid) {
        setFileErrorMessage(
          validationResult.message || 'File validation failed'
        );
        setShowFileTypeError(true);
        event.target.value = '';
        return;
      }

      processFile(file, (imageDataUrl, compressedFile, metrics) => {
        setUploadMetrics(metrics);
        if (type === CardCaptureStepEnum.vehiclePhoto) {
          const vehicleIndex = _indexOf(
            vehiclePhotoList,
            VEHICLE_PHOTO_LIST[vehiclePosition - 1]
          );
          const nextIndex =
            VehiclePhoneMappings[
              vehiclePhotoList[
                vehicleIndex + 1
              ] as keyof typeof VehiclePhoneMappings
            ];
          setVehiclePosition({
            ...vehicle,
            vehiclePosition: nextIndex
          });
        } else if (type === CardCaptureStepEnum.marinePhoto) {
          // Set the current position as done before moving to next
          const marineKey = getMarinePhotoStepKey(type, vehiclePosition);
          if (marineKey) {
            const latest = useSellerStore.getState();
            const marineSetters = {
              marineFront: setMarineImageFront,
              marineRight: setMarineImageRight,
              marineLeft: setMarineImageLeft,
              marineBack: setMarineImageBack
            };
            marineSetters[marineKey]({ ...latest[marineKey], isDone: true });
          }
          const currentIndexedPhoto = MARINE_PHOTO_LIST[vehicle.vehiclePosition - 1];
          const currentIndexInVehiclePhotoList = _indexOf(vehiclePhotoList, currentIndexedPhoto);
          if (vehiclePhotoList.length > currentIndexInVehiclePhotoList + 1) {
            const nextIndexedPhoto = vehiclePhotoList[currentIndexInVehiclePhotoList + 1];
            const nextIndex = _indexOf(MARINE_PHOTO_LIST, nextIndexedPhoto) + 1;
            setVehiclePosition({
              ...vehicle,
              vehiclePosition: nextIndex
            });
          }
        }
        setLoadImage(imageDataUrl);
        setStep(2);
      });
    } else {
      console.log('No file selected.');
    }
  };

  const renderSuccessMessage = () =>
    getCaptureSuccessMessage(type, assetType, vehiclePosition);

  const renderNextPositionMessage = () => {
    if (type === CardCaptureStepEnum.marinePhoto) {
      if (nextVehiclePosition === 2) {
        return 'starboard side of the watercraft.';
      }
      if (nextVehiclePosition === 3) {
        return 'port side of the watercraft.';
      }
      if (nextVehiclePosition === 4) {
        return 'back of the watercraft.';
      }
      return '';
    }

    if (nextVehiclePosition === 2) {
      return (
        getAssetSpecificContent(
          assetType as AssetTypesEnum
        ).sides.driver.toLowerCase() +
        ' of the ' +
        getAssetSpecificContent(assetType as AssetTypesEnum).lowerTitle +
        '.'
      );
    }
    if (nextVehiclePosition === 3) {
      return (
        getAssetSpecificContent(
          assetType as AssetTypesEnum
        ).sides.passenger.toLowerCase() +
        ' of the ' +
        getAssetSpecificContent(assetType as AssetTypesEnum).lowerTitle +
        '.'
      );
    }
    if (nextVehiclePosition === 4) {
      return (
        'back of the ' +
        getAssetSpecificContent(assetType as AssetTypesEnum).lowerTitle +
        '.'
      );
    }
    return '';
  };

  const renderVehicleImage = () => {
    const imageMap: Record<string, Record<number, string>> = {
      [AssetTypesEnum.CAR]: {
        2: VehicleRight,
        3: VehicleLeft,
        4: VehicleBack
      },
      [AssetTypesEnum.MOTOR_CYCLE]: {
        2: BikeRight,
        3: BikeLeft,
        4: BikeBack
      },
      [AssetTypesEnum.CAMPERVAN]: {
        2: CaravanRight,
        3: CaravanLeft,
        4: CaravanBack
      },
      [AssetTypesEnum.TRAILER]: {
        2: TrailerRight,
        3: TrailerLeft,
        4: TrailerBack
      },
      [AssetTypesEnum.MARINE]: {
        2: BoatRight,
        3: BoatLeft,
        4: BoatBack
      }
    };

    const assetImages = imageMap[assetType as string];
    if (assetImages && assetImages[nextVehiclePosition]) {
      return <img src={assetImages[nextVehiclePosition]} />;
    }
    return null;
  };

  const refreshInspectionData = (): Promise<void> => {
    if (!inspectionId) return Promise.resolve();
    return refreshInspection(
      inspectionId,
      post,
      { GET_INSPECTION, GET_INSPECTION_CHECK, GET_INSPECTION_FILES },
      { vehicleFront, vehicleDriver, vehiclePassenger, vehicleBack, marineFront, marineBack, marineLeft, marineRight },
      {
        setData, setInspectionChecks,
        setVehicleImageFront, setVehicleImageDriver, setVehicleImagePassenger, setVehicleImageBack,
        setMarineImageFront, setMarineImageBack, setMarineImageLeft, setMarineImageRight
      },
      getSalePriceContext(useSellerStore.getState())
    );
  };

  const markVehiclePhotoDone = (photoType: string, position: number) => {
    const key = getVehiclePhotoStepKey(photoType, position);
    if (!key) return;
    const latest = useSellerStore.getState();
    const setters: Record<
      NonNullable<ReturnType<typeof getVehiclePhotoStepKey>>,
      (data: any) => void
    > = {
      vehicleFront: setVehicleImageFront,
      vehicleDriver: setVehicleImageDriver,
      vehiclePassenger: setVehicleImagePassenger,
      vehicleBack: setVehicleImageBack
    };
    setters[key]({ ...latest[key], isDone: true });
  };

  const markMarinePhotoDone = (photoType: string, position: number) => {
    const key = getMarinePhotoStepKey(photoType, position);
    if (!key) return;
    const latest = useSellerStore.getState();
    const setters: Record<
      NonNullable<ReturnType<typeof getMarinePhotoStepKey>>,
      (data: any) => void
    > = {
      marineFront: setMarineImageFront,
      marineRight: setMarineImageRight,
      marineLeft: setMarineImageLeft,
      marineBack: setMarineImageBack
    };
    setters[key]({ ...latest[key], isDone: true });
  };

  const handleContinue = async () => {
    await refreshInspectionData();

    // Mark the just-captured marine side locally. CaptureResult also does this,
    // but the last-photo path used to return before marinePositionMap ran, so a
    // lagged GET_INSPECTION_FILES left the side incomplete and skipped Welcome.
    if (
      type === CardCaptureStepEnum.vehiclePhoto ||
      isVehicleAssetPhotoType(type)
    ) {
      markVehiclePhotoDone(type, vehiclePosition);
    }
    if (
      type === CardCaptureStepEnum.marinePhoto ||
      isMarinePhotoType(type)
    ) {
      markMarinePhotoDone(type, vehiclePosition);
    }

    const store = useSellerStore.getState();
    const requiredDocumentTypes = store.documentTypes ?? documentTypes;
    const allDocumentStepsDone = isSellerCaptureSectionComplete(store, type);

    const restoreSectionTitle = () => {
      setActivePageTitle(
        getVehicleSectionPageTitle(
          store.assetType ?? assetType,
          store.vehicleMode
        )
      );
    };

    if (type === CardCaptureStepEnum.marinePhoto &&
      vehiclePosition === vehiclePhotoList.length) {
      if (allDocumentStepsDone) {
        setPage(SellerPage.welcome);
        return;
      }
      init('init');
      restoreSectionTitle();
      setPage(SellerPage.vehicle);
      return;
    }

    if (
      (type === CardCaptureStepEnum.vehiclePhoto &&
        vehiclePosition <
          VehiclePhoneMappings[
            vehiclePhotoList[
              vehiclePhotoList.length - 1
            ] as keyof typeof VehiclePhoneMappings
          ]) ||
      (type === CardCaptureStepEnum.marinePhoto && vehiclePosition < 4)
    ) {
      if (captureImageBtn.current) {
        setTimeout(() => captureImageBtn.current?.click(), 300);
      }
    } else {
      const navigateToVehicleHome = () => {
        init('init');
        restoreSectionTitle();
        setPage(SellerPage.vehicle);
      };

      const navigateToWelcome = () => {
        setPage(SellerPage.welcome);
      };

      if (allDocumentStepsDone) {
        navigateToWelcome();
        return;
      }

      // More captures may follow; reset wizard so downstream init()/routing can run from instruction step.
      setStep(CARD_CAPTURE_INSTRUCTION_STEP);

      const vehicleIndex = _indexOf(VEHICLE_PHOTO_LIST, vehiclePhotoList[0]);
      setVehiclePosition({ ...vehicle, vehiclePosition: vehicleIndex + 1 });

      if (
        store.odometer.isDone &&
        store.vehicleFrontSeats.isDone &&
        store.vehicleBackSeats.isDone &&
        store.vehicleBoot.isDone
      ) {
        setExternalPhotos({ ...externalPhotos, isDone: true });
      }
      if (
        areAllVehicleAssetPhotosDone(requiredDocumentTypes, {
          vehicleFront: store.vehicleFront,
          vehicleBack: store.vehicleBack,
          vehicleDriver: store.vehicleDriver,
          vehiclePassenger: store.vehiclePassenger
        })
      ) {
        setVehicle({
          ...useSellerStore.getState().vehicle,
          isDone: true
        });
      }
      if (type === CardCaptureStepEnum.marinePhoto) {
        // Side was already marked via markMarinePhotoDone (fresh getState).
        // Do not re-apply with render-time closures — that can overwrite newer store state.
        const latest = useSellerStore.getState();
        if (areAllConfiguredMarinePhotosDone(latest.vehiclePhotoList ?? vehiclePhotoList, {
          marineFront: latest.marineFront,
          marineBack: latest.marineBack,
          marineLeft: latest.marineLeft,
          marineRight: latest.marineRight
        })) {
          setExternalPhotos({ ...externalPhotos, isDone: true });
        }
      }

      if (!isInternalPhotosHidden(requiredDocumentTypes) && isInternalPhotoType(type)) {
        if (isInternalPhotosSuccess({
          odometer: store.odometer,
          vehicleFrontSeats: store.vehicleFrontSeats,
          vehicleBackSeats: store.vehicleBackSeats,
          vehicleBoot: store.vehicleBoot,
          vehicleLivingArea: store.vehicleLivingArea,
          vehicleKitchen: store.vehicleKitchen,
          vehicleSleepingArea: store.vehicleSleepingArea
        }, requiredDocumentTypes)) {
          navigateToVehicleHome();
        } else {
          init('internal-photos');
          setActivePageTitle(SellerPageTitle.InternalPhotos);
        }
        return;
      }

      if (isVehicleAssetPhotoType(type)) {
        if (areAllVehicleAssetPhotosDone(requiredDocumentTypes, {
          vehicleFront: store.vehicleFront,
          vehicleBack: store.vehicleBack,
          vehicleDriver: store.vehicleDriver,
          vehiclePassenger: store.vehiclePassenger
        })) {
          navigateToVehicleHome();
        } else {
          setActivePageTitle(SellerPageTitle.ExternalPhotos);
          init('vehicles-photos');
        }
        return;
      }

      if (isMarinePhotoType(type)) {
        // Re-read after markMarinePhotoDone so completion uses the latest isDone flags.
        const afterMarine = useSellerStore.getState();
        if (areAllConfiguredMarinePhotosDone(afterMarine.vehiclePhotoList ?? vehiclePhotoList, {
          marineFront: afterMarine.marineFront,
          marineBack: afterMarine.marineBack,
          marineLeft: afterMarine.marineLeft,
          marineRight: afterMarine.marineRight
        })) {
          navigateToVehicleHome();
        } else {
          setActivePageTitle(SellerPageTitle.ExternalPhotos);
          init('marine-photos');
        }
        return;
      }

      navigateToVehicleHome();
    }
  };

  return (
    <div className="fixed inset-0 flex flex-col">
      <div className="flex-1 overflow-hidden">
        <div className="h-full flex flex-col justify-center items-center px-4 py-4">
          <SuccessIcon />
          <h2 className="text-center font-semibold text-[20px] mt-5">
            Success
          </h2>
          <div className="gap-2 flex flex-col text-center text-sm mt-5">
            <p className="text-center text-sm">
              {renderSuccessMessage()}
            </p>
            {(type === CardCaptureStepEnum.vehiclePhoto ||
              type === CardCaptureStepEnum.marinePhoto) &&
              vehiclePosition < 4 && (
                <>
                  <p className="text-gray-600 text-sm mt-6">
                    Next, take a photo of the
                  </p>
                  <p className="text-gray-600 font-bold text-[18px]">
                    {renderNextPositionMessage()}
                  </p>
                  <p className="flex justify-center items-center mt-4">
                    {renderVehicleImage()}
                  </p>
                </>
              )}
          </div>
        </div>
      </div>
      <div className="flex-shrink-0 w-full px-4 py-4 bg-grey-50 flex flex-col gap-4 lg:relative lg:w-full lg:justify-end tablet-portrait:w-[361px] tablet-portrait:justify-center tablet-portrait:px-0">
        <input
          ref={captureImageBtn}
          type="file"
          capture="environment"
          accept=".jpg,.jpeg,.png,image/jpeg,image/png"
          style={{ display: 'none' }}
          onChange={handleCaptureNewImage}
        />
        <Button
          onClick={handleContinue}
          className={clsx(
            'font-semibold w-auto normal-case rounded text-base shadow-none py-[10px] px-[18px]',
            primaryColor && `bg-[${primaryColor}]`
          )}
          style={{ backgroundColor: primaryColor }}>
          Continue
        </Button>
      </div>

      <FileTypeErrorModal
        open={showFileTypeError}
        onClose={() => setShowFileTypeError(false)}
        primaryColor={primaryColor}
        message={fileErrorMessage}
      />
    </div>
  );
};

export default CaptureComplete;
